import React from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import CreateBlockTimeModal, {
  BLOCK_TIME_SCHEDULE_PT,
  BLOCK_TIME_SCROLL_SELECT_DATE,
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";
import CreatePtSessionModal from "./CreatePtSessionModal";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { PtSessionListSkeleton } from "./SchedulingSkeletons";
import {
  consumeSchedulingCreateIntent,
  consumeSchedulingEditIntent,
} from "./scheduling-create-from-overview";
import {
  buildDuplicatePtSessionPayload,
  resolvePtSessionWindowsForDuplicate,
} from "./scheduling-duplicate-payloads";
import { canManageSchedulingRecord } from "./scheduling-event-ownership";
import { buildSchedulingCalendarPreviewUrl } from "./scheduling-preview-url";

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
/** API day_of_week: Monday = 0 … Sunday = 6 (same as Create PT Session) */
const DAY_INDEX_TO_KEY = WEEKDAY_KEYS;
const WEEKDAY_LABELS = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

/**
 * Maps GET …/availability-windows body (`windows` + `weekly_availability`) to
 * `weeklySlots` shape: { mon: string[], … } for the expanded grid.
 */
function normalizeAvailabilityToWeeklySlots(payload) {
  if (!payload || typeof payload !== "object") return null;

  const wa = payload.weekly_availability ?? payload.weeklyAvailability;
  if (wa && typeof wa === "object") {
    const out = {};
    for (const key of WEEKDAY_KEYS) {
      const capKey = key.charAt(0).toUpperCase() + key.slice(1);
      const raw = wa[key] ?? wa[capKey] ?? wa[key.toUpperCase()];
      if (raw == null) continue;
      if (Array.isArray(raw)) {
        out[key] = raw.map((x) =>
          typeof x === "object" && x !== null
            ? formatWindowSlot(x)
            : formatTimeRangeStringToAmPm(String(x))
        );
      } else if (typeof raw === "string" && raw.trim()) {
        out[key] = [formatTimeRangeStringToAmPm(raw.trim())];
      }
    }
    if (Object.keys(out).length > 0) return out;
  }

  const windows = payload.windows;
  if (!Array.isArray(windows) || windows.length === 0) return null;

  const byDay = {};
  for (const w of windows) {
    const dow = w.day_of_week ?? w.dayOfWeek;
    if (dow == null || dow < 0 || dow > 6) continue;
    const key = DAY_INDEX_TO_KEY[dow];
    if (!key) continue;
    const label = formatWindowSlot(w);
    if (!label) continue;
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(label);
  }
  return Object.keys(byDay).length > 0 ? byDay : null;
}

/** Parse `HH:mm` or `HH:mm:ss` (24h) for display conversion. */
function parseHhMm24(timeStr) {
  if (timeStr == null || typeof timeStr !== "string") return null;
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min) || h > 23 || min > 59) return null;
  return { h, min };
}

/** Single clock time → `h:mm AM/PM` (e.g. `9:00 AM`, `12:30 PM`). */
function formatSingleTimeToAmPm(timeStr) {
  const p = parseHhMm24(timeStr);
  if (!p) return typeof timeStr === "string" ? timeStr : "";
  const period = p.h >= 12 ? "PM" : "AM";
  const hour12 = p.h % 12 || 12;
  return `${hour12}:${String(p.min).padStart(2, "0")} ${period}`;
}

/**
 * Turn free-text or range strings that contain 24h times into AM/PM
 * (e.g. list `time_label` `09:00 – 17:00` → `9:00 AM – 5:00 PM`).
 * Leaves non-matching text unchanged.
 */
function formatTimeRangeStringToAmPm(s) {
  if (s == null || typeof s !== "string") return s;
  if (s === "—") return s;
  /* Already 12h from API or copy */
  if (/\b(am|pm)\b/i.test(s)) return s;
  return s.replace(/\b(\d{1,2}):(\d{2})(?::\d{2})?\b/g, (full) => {
    const p = parseHhMm24(full);
    if (!p) return full;
    return formatSingleTimeToAmPm(full);
  });
}

function formatWindowSlot(w) {
  const start = w.start_time ?? w.startTime ?? "";
  const end = w.end_time ?? w.endTime ?? "";
  if (start && end) {
    return `${formatSingleTimeToAmPm(start)} – ${formatSingleTimeToAmPm(end)}`;
  }
  if (start || end) {
    const a = start ? formatSingleTimeToAmPm(start) : "";
    const b = end ? formatSingleTimeToAmPm(end) : "";
    return `${a}${b}`;
  }
  return "";
}

/** If all active days share the same single time slot, return that slot string.
 *  If slots are loaded but differ across days, return "Hours Vary".
 *  If no data, return null. */
function resolveTimeLabel(weeklySlots) {
  if (!weeklySlots || typeof weeklySlots !== "object") return null;
  const activeDays = WEEKDAY_KEYS.filter(
    (key) => Array.isArray(weeklySlots[key]) && weeklySlots[key].length > 0
  );
  if (activeDays.length === 0) return null;
  const allSingleSlot = activeDays.every(
    (key) => weeklySlots[key].length === 1
  );
  if (!allSingleSlot) return "Hours Vary";
  const firstSlot = weeklySlots[activeDays[0]][0];
  const allSame = activeDays.every((key) => weeklySlots[key][0] === firstSlot);
  return allSame ? firstSlot : "Hours Vary";
}

/** Comma-separated weekday abbreviations for days that have at least one slot (for list “Days” column). */
function formatDaysWithAvailability(weeklySlots) {
  if (!weeklySlots || typeof weeklySlots !== "object") return null;
  const labels = [];
  for (const key of WEEKDAY_KEYS) {
    const slots = weeklySlots[key];
    if (Array.isArray(slots) && slots.length > 0) {
      labels.push(WEEKDAY_LABELS[key]);
    }
  }
  return labels.length > 0 ? labels.join(", ") : null;
}

/**
 * Coach headshot: profile avatar (S3) when `coach` / `activeUser` has avatar data,
 * else a usable URL from the session payload (not the generic user-circle fallback).
 */
function resolveCoachProfilePhotoUrl(row, activeUser, theme) {
  const coachUser = row.coach ?? activeUser;
  if (coachUser?.avatar?.s3Path || coachUser?.athleteAvatar?.s3Path) {
    return getAvatarUrl(coachUser, false, theme, theme !== "dark");
  }
  const explicit = row.coachAvatar;
  if (
    typeof explicit === "string" &&
    explicit.length > 0 &&
    !explicit.includes("user-circle.svg")
  ) {
    return explicit;
  }
  return getAvatarUrl(coachUser, false, theme, theme !== "dark");
}

function extractPtCoachList(rawCoaches, theme) {
  if (!Array.isArray(rawCoaches)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of rawCoaches) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim(),
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

/** Map API PT session document to card props (extend when backend shape is fixed) */
function sessionToCard(s, theme) {
  const id = s._id ?? s.id;
  const coaches = extractPtCoachList(s.coaches, theme);
  const coachCount = Math.max(1, coaches.length);
  const capacityNum = s.capacity != null && s.capacity > 0 ? s.capacity : 1;
  return {
    id,
    raw: s,
    title: s.name ?? s.title ?? "Untitled session",
    coachAvatar:
      s.coach_avatar_url ?? s.thumbnail_url ?? "/img/new-icons/user-circle.svg",
    coach: s.coach ?? null,
    coaches,
    coachesExtra: coaches.length > 3 ? coaches.length - 3 : null,
    durationLabel:
      s.duration_minutes != null ? `${s.duration_minutes} mins` : "—",
    ratioLabel: s.ratio_label ?? s.ratio ?? `${coachCount}:${capacityNum}`,
    status: s.status === "paused" ? "paused" : "active",
    scheduleType: s.schedule_type ?? s.scheduleType ?? "—",
    daysLabel: s.days_label ?? s.days ?? "—",
    timeLabel: s.time_label ?? s.time ?? "—",
    weeklySlots: s.weekly_slots ?? s.weeklySlots ?? null,
  };
}

const SchedulingPtSessions = ({
  hasNonPullScheduling = true,
  hasPressScheduling = false,
  hasPullScheduling = false,
  canPushUser = true,
  canPullUser = true,
}) => {
  const showPermissionsAction = (event) =>
    hasNonPullScheduling &&
    (hasPressScheduling || hasPullScheduling || Boolean(event?.is_restricted));
  const { locals } = useRillContext();
  const activeUser = locals?.activeUser ?? {};
  const theme = activeUser.webAppTheme || "dark";
  const canManageSession = (row) =>
    canManageSchedulingRecord(row?.raw, activeUser);

  const [createPtModalOpen, setCreatePtModalOpen] = React.useState(false);
  const [editingPtSession, setEditingPtSession] = React.useState(null);
  const [ptModalInitialScrollSection, setPtModalInitialScrollSection] =
    React.useState(null);
  const [blockTimeModalOpen, setBlockTimeModalOpen] = React.useState(false);
  const [blockTimePreset, setBlockTimePreset] = React.useState(null);
  const [activeFilter, setActiveFilter] = React.useState("active");
  const [expandedId, setExpandedId] = React.useState(null);
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState("");
  const [filterDurMin, setFilterDurMin] = React.useState("");
  const [filterDurMax, setFilterDurMax] = React.useState("");
  const filterPanelRef = React.useRef(null);

  React.useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);
  /** Per-session availability from GET …/availability-windows (prefetch + expand) */
  const [availabilityById, setAvailabilityById] = React.useState({});
  const availabilityByIdRef = React.useRef(availabilityById);
  availabilityByIdRef.current = availabilityById;

  const fetchSessions = React.useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const res = await fetch("/actions/scheduling/pt-sessions", {
        method: "GET",
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setSessions([]);
        return;
      }
      if (!res.ok) {
        const msg =
          data?.error ??
          data?.message ??
          (typeof data === "string" ? data : "Failed to load PT sessions");
        throw new Error(msg);
      }
      const list = Array.isArray(data) ? data : [];
      setSessions(list.map((s) => sessionToCard(s, theme)));
    } catch (e) {
      setLoadError(e?.message ?? "Failed to load PT sessions");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [theme]);

  React.useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  React.useEffect(() => {
    if (consumeSchedulingCreateIntent("pt")) {
      setEditingPtSession(null);
      setPtModalInitialScrollSection(null);
      setCreatePtModalOpen(true);
    }
  }, []);

  const pendingEditIdRef = React.useRef(consumeSchedulingEditIntent("pt"));

  const deletePtSession = React.useCallback(
    async (row) => {
      const id = row.raw?._id ?? row.id;
      if (!id) return;
      if (!window.confirm("Delete this PT session? This cannot be undone.")) {
        return;
      }
      try {
        const res = await fetch(
          `/actions/scheduling/pt-sessions/${encodeURIComponent(String(id))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to delete PT session"
          );
        }
        setMenuOpenId(null);
        setExpandedId((prev) => (prev === id ? null : prev));
        setAvailabilityById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        fetchSessions();
      } catch (e) {
        window.alert(e?.message ?? "Failed to delete PT session");
      }
    },
    [fetchSessions]
  );

  const coachId = activeUser?._id != null ? String(activeUser._id) : "";

  const duplicatePtSession = React.useCallback(
    async (row) => {
      if (!row?.raw) return;
      setMenuOpenId(null);
      try {
        const windows = await resolvePtSessionWindowsForDuplicate(
          row,
          availabilityById
        );
        if (windows.length === 0) {
          window.alert(
            "Add availability windows to this PT session before duplicating."
          );
          return;
        }
        const payload = buildDuplicatePtSessionPayload(
          row.raw,
          coachId,
          windows
        );
        if (!payload.coach_id) {
          window.alert("You must be signed in as a coach to duplicate.");
          return;
        }
        const res = await fetch("/actions/scheduling/pt-sessions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to duplicate PT session"
          );
        }
        fetchSessions();
      } catch (e) {
        window.alert(e?.message ?? "Failed to duplicate PT session");
      }
    },
    [fetchSessions, coachId, availabilityById]
  );

  /** After list loads, prefetch availability-windows per session so “Days” can list weekdays with slots. */
  React.useEffect(() => {
    if (loading || sessions.length === 0) return;
    let cancelled = false;
    (async () => {
      await Promise.all(
        sessions.map(async (s) => {
          if (!s?.id) return;
          try {
            const res = await fetch(
              `/actions/scheduling/pt-sessions/${encodeURIComponent(s.id)}/availability-windows`,
              { method: "GET", credentials: "same-origin" }
            );
            const data = await res.json().catch(() => ({}));
            if (cancelled) return;
            if (!res.ok) return;
            const weeklySlots = normalizeAvailabilityToWeeklySlots(data);
            setAvailabilityById((prev) => ({
              ...prev,
              [s.id]: {
                ...prev[s.id],
                loading: false,
                error: null,
                weeklySlots,
                raw: data,
              },
            }));
          } catch {
            /* ignore per-row */
          }
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, sessions]);

  React.useEffect(() => {
    if (!expandedId) return;
    const existing = availabilityByIdRef.current[expandedId];
    if (existing?.raw !== undefined && existing?.error == null) {
      return;
    }
    let cancelled = false;
    setAvailabilityById((prev) => ({
      ...prev,
      [expandedId]: {
        ...(prev[expandedId] || {}),
        loading: true,
        error: null,
      },
    }));
    const url = `/actions/scheduling/pt-sessions/${encodeURIComponent(expandedId)}/availability-windows`;
    fetch(url, { method: "GET", credentials: "same-origin" })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          const msg =
            data?.error ??
            data?.message ??
            (typeof data === "string" ? data : "Failed to load availability");
          throw new Error(msg);
        }
        const weeklySlots = normalizeAvailabilityToWeeklySlots(data);
        setAvailabilityById((prev) => ({
          ...prev,
          [expandedId]: {
            loading: false,
            error: null,
            weeklySlots,
            raw: data,
          },
        }));
      })
      .catch((e) => {
        if (cancelled) return;
        setAvailabilityById((prev) => ({
          ...prev,
          [expandedId]: {
            loading: false,
            error: e?.message ?? "Failed to load availability",
            weeklySlots: null,
          },
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [expandedId]);

  const filteredSessions = (() => {
    const base =
      activeFilter === "expired"
        ? sessions.filter((s) => s.status !== "active")
        : sessions.filter((s) => s.status === "active");
    const q = filterQuery.trim().toLowerCase();
    const minDur = filterDurMin ? Number(filterDurMin) : null;
    const maxDur = filterDurMax ? Number(filterDurMax) : null;
    return base.filter((s) => {
      if (
        q &&
        !String(s.title || "")
          .toLowerCase()
          .includes(q)
      )
        return false;
      if (minDur != null || maxDur != null) {
        const dur =
          s.raw?.duration_minutes != null
            ? Number(s.raw.duration_minutes)
            : null;
        if (dur == null) return false;
        if (minDur != null && dur < minDur) return false;
        if (maxDur != null && dur > maxDur) return false;
      }
      return true;
    });
  })();

  const ptFilterCount =
    (filterQuery.trim() ? 1 : 0) +
    (filterDurMin ? 1 : 0) +
    (filterDurMax ? 1 : 0);

  const openEditPtSession = React.useCallback(
    async (row, scrollSection = null) => {
      const base = row.raw && typeof row.raw === "object" ? { ...row.raw } : {};
      let merged = base;
      const cached = availabilityById[row.id]?.raw;
      if (
        cached &&
        typeof cached === "object" &&
        Array.isArray(cached.windows) &&
        cached.windows.length > 0
      ) {
        merged = {
          ...merged,
          windows: cached.windows.map((w) => ({ ...w })),
        };
      } else if (
        !Array.isArray(merged.windows) ||
        merged.windows.length === 0
      ) {
        try {
          const res = await fetch(
            `/actions/scheduling/pt-sessions/${encodeURIComponent(row.id)}/availability-windows`,
            { method: "GET", credentials: "same-origin" }
          );
          const data = await res.json().catch(() => ({}));
          if (
            res.ok &&
            Array.isArray(data.windows) &&
            data.windows.length > 0
          ) {
            merged = {
              ...merged,
              windows: data.windows.map((w) => ({ ...w })),
            };
          }
        } catch {
          /* ignore */
        }
      }
      setPtModalInitialScrollSection(scrollSection);
      setEditingPtSession(merged);
      setCreatePtModalOpen(true);
    },
    [availabilityById]
  );

  React.useEffect(() => {
    const id = pendingEditIdRef.current;
    if (!id || sessions.length === 0) return;
    const match = sessions.find(
      (s) => String(s.raw?._id ?? s.id) === String(id)
    );
    if (match) {
      pendingEditIdRef.current = null;
      if (!canManageSchedulingRecord(match.raw, activeUser)) return;
      openEditPtSession(match);
    }
  }, [sessions, openEditPtSession, activeUser]);

  return (
    <div className="scheduling-classes scheduling-pt-sessions">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">PT Sessions</h1>
          <p className="scheduling-classes-subtitle">
            Manage and schedule personal training sessions.
          </p>
          <div className="scheduling-classes-header-buttons">
            <button
              type="button"
              data-button-type="primary"
              className="scheduling-classes-create-btn"
              onClick={() => {
                setEditingPtSession(null);
                setPtModalInitialScrollSection(null);
                setCreatePtModalOpen(true);
              }}
            >
              + Create PT Session
            </button>
            <button
              type="button"
              data-button-type="outline"
              className="scheduling-classes-block-time-btn"
              onClick={() => {
                setBlockTimePreset({ scheduleKind: BLOCK_TIME_SCHEDULE_PT });
                setBlockTimeModalOpen(true);
              }}
            >
              <img
                src="/img/new-icons/slash.svg"
                alt=""
                className="scheduling-classes-block-icon"
              />
              Block Time : PT Session
              <span className="scheduling-classes-info-icon">
                <img
                  src="/img/new-icons/vuesax-linear-tick-circle-schedule.svg"
                  alt="Info"
                />
                <span
                  className="scheduling-classes-info-tooltip"
                  role="tooltip"
                >
                  Block time for all the PT Session. This will not block time
                  for classes and facilities.
                </span>
              </span>
            </button>
          </div>
        </hgroup>
      </header>

      <div className="scheduling-classes-box">
        <nav className="scheduling-classes-tabs">
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "active" ? "page" : undefined}
            onClick={() => setActiveFilter("active")}
          >
            Active
          </button>
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "expired" ? "page" : undefined}
            onClick={() => setActiveFilter("expired")}
          >
            Expired
          </button>
          <div className="scheduling-classes-filter-wrap" ref={filterPanelRef}>
            <button
              type="button"
              className="scheduling-classes-filter-btn"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((o) => !o)}
            >
              <img src="/img/new-icons/filter-white.svg" alt="" />
              Filter
              {ptFilterCount > 0 && (
                <span className="scheduling-classes-filter-badge">
                  {ptFilterCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="scheduling-classes-filter-panel" role="dialog">
                <label className="scheduling-classes-filter-label">
                  Filter by name
                  <input
                    type="text"
                    className="scheduling-classes-filter-input"
                    placeholder="Search PT sessions…"
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    autoFocus
                  />
                </label>
                <div className="scheduling-classes-filter-row">
                  <label className="scheduling-classes-filter-label">
                    Min duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 30"
                      value={filterDurMin}
                      onChange={(e) => setFilterDurMin(e.target.value)}
                    />
                  </label>
                  <label className="scheduling-classes-filter-label">
                    Max duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 90"
                      value={filterDurMax}
                      onChange={(e) => setFilterDurMax(e.target.value)}
                    />
                  </label>
                </div>
                <footer className="scheduling-classes-filter-footer">
                  <button
                    type="button"
                    data-button-type="tertiary"
                    onClick={() => {
                      setFilterQuery("");
                      setFilterDurMin("");
                      setFilterDurMax("");
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setFilterOpen(false)}
                  >
                    Done
                  </button>
                </footer>
              </div>
            )}
          </div>
        </nav>

        {loading && <PtSessionListSkeleton theme={theme} />}
        {!loading && loadError && (
          <p className="scheduling-classes-error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && sessions.length === 0 && (
          <SchedulingEmptyState
            iconSrc="/img/new-icons/pt-session-icon.svg"
            title="No PT sessions yet"
            description="Create your first PT session to start booking 1-on-1s."
            action={
              <button
                type="button"
                data-button-type="primary"
                onClick={() => setCreatePtModalOpen(true)}
              >
                + Create PT Session
              </button>
            }
          />
        )}
        <ul className="scheduling-classes-list scheduling-pt-sessions-list">
          {filteredSessions.map((row) => (
            <li key={row.id} className="scheduling-pt-session-row-wrap">
              <div className="scheduling-class-card scheduling-pt-session-card">
                <div className="scheduling-pt-session-row-lead">
                  <div className="scheduling-class-card-checkbox">
                    <input
                      type="checkbox"
                      id={`pt-${row.id}`}
                      aria-label={`Select ${row.title}`}
                      disabled={!canManageSession(row)}
                    />
                  </div>
                  <button
                    type="button"
                    className="scheduling-pt-session-expand-btn"
                    aria-expanded={expandedId === row.id}
                    aria-label={
                      expandedId === row.id
                        ? "Collapse weekly schedule"
                        : "Expand weekly schedule"
                    }
                    onClick={() =>
                      setExpandedId(expandedId === row.id ? null : row.id)
                    }
                  >
                    <img
                      src={
                        expandedId === row.id
                          ? "/img/new-icons/vuesax-linear-arrow-square-down.svg"
                          : "/img/new-icons/vuesax-linear-arrow-square-right.svg"
                      }
                      alt=""
                    />
                  </button>
                  {row.coaches.length > 1 ? (
                    <div
                      className={
                        row.coaches.length === 2
                          ? "scheduling-pt-session-avatar-cluster scheduling-pt-session-avatar-cluster--2"
                          : "scheduling-pt-session-avatar-cluster scheduling-pt-session-avatar-cluster--3"
                      }
                    >
                      {row.coaches.slice(0, 3).map((c, i) => (
                        <span
                          key={c.id}
                          className={`scheduling-pt-session-avatar-cluster-item scheduling-pt-session-avatar-cluster-item--${i}`}
                          title={c.name}
                        >
                          <img src={c.avatarUrl} alt="" loading="lazy" />
                        </span>
                      ))}
                      {row.coaches.length > 3 && (
                        <span
                          className="scheduling-pt-session-avatar-cluster-extra"
                          title={`+${row.coaches.length - 3} more`}
                        >
                          +{row.coaches.length - 3}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="scheduling-class-card-thumb scheduling-pt-session-avatar">
                      <img
                        src={
                          row.coaches[0]?.avatarUrl ??
                          resolveCoachProfilePhotoUrl(row, activeUser, theme)
                        }
                        alt=""
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
                <div className="scheduling-pt-session-identity">
                  <h3 className="scheduling-class-card-title">{row.title}</h3>
                  <div className="scheduling-pt-session-meta">
                    <span className="scheduling-pt-session-meta-text">
                      {row.durationLabel} · {row.ratioLabel}
                    </span>
                    <span
                      className={
                        "scheduling-pt-session-status" +
                        (row.status === "paused"
                          ? " scheduling-pt-session-status--paused"
                          : " scheduling-pt-session-status--active")
                      }
                    >
                      {row.status === "paused" ? "Paused" : "Active"}
                    </span>
                  </div>
                </div>
                <div className="scheduling-pt-session-columns">
                  <div className="scheduling-pt-session-col">
                    <span className="scheduling-pt-session-col-label">
                      Schedule
                    </span>
                    <span className="scheduling-pt-session-col-value">
                      {row.scheduleType}
                    </span>
                  </div>
                  <div className="scheduling-pt-session-col">
                    <span className="scheduling-pt-session-col-label">
                      Days
                    </span>
                    <span className="scheduling-pt-session-col-value">
                      {formatDaysWithAvailability(
                        availabilityById[row.id]?.weeklySlots
                      ) ??
                        formatDaysWithAvailability(row.weeklySlots) ??
                        row.daysLabel}
                    </span>
                  </div>
                  <div className="scheduling-pt-session-col">
                    <span className="scheduling-pt-session-col-label">
                      Time
                    </span>
                    <span className="scheduling-pt-session-col-value">
                      {resolveTimeLabel(
                        availabilityById[row.id]?.weeklySlots ?? row.weeklySlots
                      ) ?? formatTimeRangeStringToAmPm(row.timeLabel)}
                    </span>
                  </div>
                </div>
                <div className="scheduling-class-card-actions">
                  {canManageSession(row) && (
                    <button
                      type="button"
                      className="scheduling-class-card-edit-btn"
                      onClick={() => openEditPtSession(row)}
                    >
                      <img src="/img/new-icons/edit-pencil.svg" alt="" />
                      Edit
                    </button>
                  )}
                  <div className="scheduling-class-card-menu-wrapper">
                    <button
                      type="button"
                      className="scheduling-class-card-menu-btn"
                      aria-expanded={menuOpenId === row.id}
                      aria-haspopup="true"
                      onClick={() =>
                        setMenuOpenId(menuOpenId === row.id ? null : row.id)
                      }
                    >
                      <img
                        src="/img/new-icons/more-square.svg"
                        alt="More actions"
                      />
                    </button>
                    {menuOpenId === row.id && (
                      <>
                        <div
                          className="scheduling-class-card-menu-backdrop"
                          onClick={() => setMenuOpenId(null)}
                          aria-hidden="true"
                        />
                        <ul className="scheduling-class-card-menu">
                          <li>
                            <a
                              href={buildSchedulingCalendarPreviewUrl(
                                "pt",
                                row.id
                              )}
                              onClick={() => setMenuOpenId(null)}
                            >
                              <img
                                src="/img/new-icons/vuesax-linear-play.svg"
                                alt=""
                              />
                              Preview in Calendar
                            </a>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => duplicatePtSession(row)}
                            >
                              <img src="/img/new-icons/duplicate.svg" alt="" />
                              Duplicate
                            </button>
                          </li>
                          {canManageSession(row) && (
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setBlockTimePreset({
                                    scheduleKind: BLOCK_TIME_SCHEDULE_PT,
                                    bookableId: String(row.id),
                                    scrollSection:
                                      BLOCK_TIME_SCROLL_SELECT_DATE,
                                  });
                                  setBlockTimeModalOpen(true);
                                }}
                              >
                                <img
                                  src="/img/new-icons/close-circle-outline-white.svg"
                                  alt=""
                                />
                                Block Time
                              </button>
                            </li>
                          )}
                          {showPermissionsAction(row.raw) &&
                            canManageSession(row) && (
                              <li>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuOpenId(null);
                                    openEditPtSession(
                                      row,
                                      "access_permissions"
                                    );
                                  }}
                                >
                                  <img src="/img/new-icons/lock.svg" alt="" />
                                  Permissions
                                </button>
                              </li>
                            )}
                          {canManageSession(row) && (
                            <li>
                              <button
                                type="button"
                                className="scheduling-class-card-menu-delete"
                                onClick={() => deletePtSession(row)}
                              >
                                <img
                                  src="/img/new-icons/red-trash.svg"
                                  alt=""
                                />
                                Delete
                              </button>
                            </li>
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {expandedId === row.id && (
                <section
                  className="scheduling-pt-session-expanded"
                  aria-label="Weekly schedule"
                >
                  {availabilityById[row.id]?.loading && (
                    <p
                      className="scheduling-pt-session-availability-loading"
                      aria-live="polite"
                    >
                      <span
                        className="scheduling-classes-loading-spinner"
                        aria-hidden
                      />
                      Loading availability…
                    </p>
                  )}
                  {availabilityById[row.id]?.error &&
                    !availabilityById[row.id]?.loading && (
                      <p
                        className="scheduling-pt-session-availability-error"
                        role="alert"
                      >
                        {availabilityById[row.id].error}
                      </p>
                    )}
                  {!availabilityById[row.id]?.loading && (
                    <div className="scheduling-pt-session-week-grid">
                      {WEEKDAY_KEYS.map((key) => {
                        const fromApi =
                          availabilityById[row.id]?.weeklySlots?.[key];
                        const fromList = row.weeklySlots?.[key];
                        const slotsFromAvailability =
                          Array.isArray(fromApi) && fromApi.length > 0;
                        const slots = slotsFromAvailability
                          ? fromApi
                          : Array.isArray(fromList) && fromList.length > 0
                            ? fromList
                            : null;
                        const hasSlots =
                          Array.isArray(slots) && slots.length > 0;
                        return (
                          <div key={key} className="scheduling-pt-session-day">
                            <span className="scheduling-pt-session-day-label">
                              {WEEKDAY_LABELS[key]}
                            </span>
                            {hasSlots ? (
                              <ul className="scheduling-pt-session-slots">
                                {slots.map((slot, i) => (
                                  <li key={`${key}-${i}-${slot}`}>
                                    {slotsFromAvailability
                                      ? slot
                                      : formatTimeRangeStringToAmPm(slot)}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="scheduling-pt-session-unavailable">
                                Unavailable
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </li>
          ))}
        </ul>
      </div>

      <CreateBlockTimeModal
        key={
          blockTimeModalOpen && blockTimePreset
            ? `bt-${blockTimePreset.scheduleKind}-${blockTimePreset.bookableId}`
            : "block-time-closed-pt"
        }
        isOpen={blockTimeModalOpen}
        listsLoading={loading}
        classes={[]}
        ptSessions={sessions
          .filter(canManageSession)
          .map((s) => s.raw)
          .filter(Boolean)}
        facilities={[]}
        showFacilities={hasNonPullScheduling}
        initialScheduleKind={blockTimePreset?.scheduleKind ?? null}
        initialPreselectedBookableId={blockTimePreset?.bookableId ?? null}
        initialScrollSection={blockTimePreset?.scrollSection ?? null}
        onClose={() => {
          setBlockTimeModalOpen(false);
          setBlockTimePreset(null);
        }}
        onConfirm={async (payloads) => {
          await submitBlockTimePayloads(payloads);
          fetchSessions();
        }}
      />

      <CreatePtSessionModal
        key={
          createPtModalOpen
            ? String(editingPtSession?._id ?? editingPtSession?.id ?? "create")
            : "closed"
        }
        isOpen={createPtModalOpen}
        initialRecord={editingPtSession}
        initialScrollSection={ptModalInitialScrollSection}
        showAccessPermissions={hasNonPullScheduling}
        hasPressScheduling={hasPressScheduling}
        hasPullScheduling={hasPullScheduling}
        canPushUser={canPushUser}
        canPullUser={canPullUser}
        onClose={() => {
          setCreatePtModalOpen(false);
          setEditingPtSession(null);
          setPtModalInitialScrollSection(null);
        }}
        onConfirm={async (payload) => {
          const editId = editingPtSession?._id ?? editingPtSession?.id;
          const url = editId
            ? `/actions/scheduling/pt-sessions/${encodeURIComponent(String(editId))}`
            : "/actions/scheduling/pt-sessions";
          const res = await fetch(url, {
            method: editId ? "PUT" : "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (editId
                  ? "Failed to update PT session"
                  : "Failed to create PT session")
            );
          }
          setEditingPtSession(null);
          fetchSessions();
        }}
      />
    </div>
  );
};

export default SchedulingPtSessions;
